import React, { useContext, useState, useRef, useEffect } from 'react'
import { AuthContext } from '../context/AuthContext'
import { NavLink, useLocation } from 'react-router-dom'
import Logo from './../assets/main-logo.png'
import {
    LayoutDashboard, Users, ShieldCheck, FileText, Rss, Radio,
    Megaphone, Newspaper, BarChart3, Video, Package, CreditCard,
    Bell, History, Flag, ScrollText, ShieldAlert, Bug,
    LogOut, Menu, X, Smartphone,
} from 'lucide-react'

// Grouped nav — icons + sections instead of one flat horizontal pill strip.
const superAdminSections = [
    {
        label: 'Overview',
        items: [
            { name: 'Dashboard', href: '/super-admin/home', icon: LayoutDashboard },
        ],
    },
    {
        label: 'Content',
        items: [
            { name: 'Updates', href: '/super-admin/updates', icon: Newspaper },
            { name: 'Feeds', href: '/super-admin/feeds', icon: Rss },
            { name: 'Polls', href: '/super-admin/polls', icon: BarChart3 },
            { name: 'Meetings', href: '/super-admin/meetings', icon: Video },
            { name: 'Ads', href: '/super-admin/ads', icon: Megaphone },
            { name: 'Breaking News', href: '/super-admin/breakingnews', icon: Radio },
            { name: 'Packages', href: '/super-admin/packages', icon: Package },
        ],
    },
    {
        label: 'Users',
        items: [
            { name: 'Users', href: '/super-admin/users', icon: Users },
            { name: 'Admins', href: '/super-admin/admins', icon: ShieldCheck },
        ],
    },
    {
        label: 'Engagement',
        items: [
            { name: 'Push Notifications', href: '/super-admin/push-notifications', icon: Bell },
            { name: 'Push History', href: '/super-admin/push-history', icon: History },
        ],
    },
    {
        label: 'Moderation',
        items: [
            { name: 'Reports', href: '/super-admin/reports', icon: Flag },
            { name: 'Audit Log', href: '/super-admin/audit-log', icon: ScrollText },
            { name: 'Failed Logins', href: '/super-admin/failed-logins', icon: ShieldAlert },
            { name: 'Crash Reports', href: '/super-admin/crash-reports', icon: Bug },
        ],
    },
    {
        label: 'Finance',
        items: [
            { name: 'Transactions', href: '/super-admin/transactions', icon: CreditCard },
        ],
    },
    {
        label: 'System',
        items: [
            { name: 'App Update', href: '/super-admin/app-config', icon: Smartphone },
        ],
    },
]

const buildAdminSections = (user) => [
    {
        label: 'Overview',
        items: [{ name: 'Dashboard', href: '/admin/home', icon: LayoutDashboard }],
    },
    {
        label: 'Modules',
        items: (user.permission || []).map(item => ({
            name: item.charAt(0).toUpperCase() + item.slice(1),
            href: `/admin/${item}`,
            icon: FileText,
        })),
    },
]

// Persist sidebar scroll position across navigations. The Navbar remounts
// when the route changes (pages render their own <Navbar />), so without
// this the scroll jumps back to the top and hides the active item.
const SCROLL_KEY = 'admin_nav_scroll'

export default function Navbar() {
    const { hadnelLogout, user } = useContext(AuthContext)
    const location = useLocation()
    const [mobileOpen, setMobileOpen] = useState(false)
    const navRef = useRef(null)

    const sections = user.isSuperAdmin ? superAdminSections : buildAdminSections(user)

    // On mount, restore scroll position.
    useEffect(() => {
        const el = navRef.current
        if (!el) return
        const saved = Number(sessionStorage.getItem(SCROLL_KEY) || 0)
        if (saved > 0) el.scrollTop = saved
        // If the active item is offscreen (different page landed us far away), scroll it into view.
        const active = el.querySelector('[aria-current="page"]')
        if (active && active.getBoundingClientRect) {
            const navBox = el.getBoundingClientRect()
            const itemBox = active.getBoundingClientRect()
            if (itemBox.top < navBox.top || itemBox.bottom > navBox.bottom) {
                active.scrollIntoView({ block: 'nearest' })
            }
        }
    }, [location.pathname])

    const SidebarBody = (
        <div className="flex flex-col h-full bg-white border-r border-gray-200">
            {/* Logo block */}
            <div className="h-16 px-5 flex items-center gap-3 border-b border-gray-200">
                <img alt="Jambhani Live" src={Logo} className="h-9 w-9 rounded" />
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">Jambhani Live</div>
                    <div className="text-xs text-gray-500 truncate">
                        {user.isSuperAdmin ? 'Super-Admin' : 'Admin'}
                    </div>
                </div>
            </div>

            {/* Nav */}
            <nav
                ref={navRef}
                className="flex-1 overflow-y-auto py-4 px-3"
                onScroll={e => sessionStorage.setItem(SCROLL_KEY, String(e.currentTarget.scrollTop))}
            >
                {sections.map(section => (
                    <div key={section.label} className="mb-5">
                        <div className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                            {section.label}
                        </div>
                        <ul className="space-y-0.5">
                            {section.items.map(item => {
                                const Icon = item.icon
                                return (
                                    <li key={item.name}>
                                        <NavLink
                                            to={item.href}
                                            onClick={() => setMobileOpen(false)}
                                            className={({ isActive }) =>
                                                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                                                    isActive
                                                        ? 'bg-gray-900 text-white'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                }`
                                            }
                                        >
                                            <Icon size={16} strokeWidth={2} />
                                            <span className="truncate">{item.name}</span>
                                        </NavLink>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                ))}
            </nav>

            {/* Logout */}
            <div className="p-3 border-t border-gray-200">
                <button
                    onClick={hadnelLogout}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                    <LogOut size={16} />
                    Logout
                </button>
            </div>
        </div>
    )

    return (
        <>
            {/* Desktop sidebar — fixed left */}
            <aside className="hidden md:flex w-60 shrink-0 fixed inset-y-0 left-0 z-20">
                {SidebarBody}
            </aside>

            {/* Mobile top bar */}
            <header className="md:hidden bg-white border-b border-gray-200 sticky top-0 z-30">
                <div className="h-14 px-4 flex items-center justify-between">
                    <button
                        onClick={() => setMobileOpen(true)}
                        className="p-2 -ml-2 rounded hover:bg-gray-100"
                        aria-label="Open menu"
                    >
                        <Menu size={20} />
                    </button>
                    <div className="flex items-center gap-2">
                        <img alt="" src={Logo} className="h-7 w-7 rounded" />
                        <span className="font-semibold text-sm">Jambhani Live</span>
                    </div>
                    <div className="w-8" />
                </div>
            </header>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div className="md:hidden fixed inset-0 z-40">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
                    <div className="absolute inset-y-0 left-0 w-72 shadow-xl">
                        <button
                            onClick={() => setMobileOpen(false)}
                            className="absolute top-3 right-3 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200"
                            aria-label="Close menu"
                        >
                            <X size={16} />
                        </button>
                        {SidebarBody}
                    </div>
                </div>
            )}
        </>
    )
}
